import type { ChannelClient } from '@hibro/platform-core';
import { createServiceProxy } from '@hibro/platform-core';
import type { DetachBridge } from '@hibro/platform-renderer';
import type { DetachedSurfaceInfo, IDetachService } from '@hibro/shared';
import { DETACH_CHANNEL, DETACHED_CHANGED_EVENT } from '@hibro/shared';

/**
 * Build the detach-mechanism bridge from the extension host's detach service
 * (§5.8 通用 Detachable).
 *
 * Runtime-neutral: the detached-surface registry lives in the host, so this is
 * the same code on the desktop and in the browser — where there is no preload at
 * all. It replaces the previous `hibroWindow` IPC wrapper, which is also why the
 * detach affordance no longer appears-but-does-nothing on the web.
 *
 * Changes arrive as the `DETACHED_CHANGED_EVENT` broadcast rather than a
 * window-scoped IPC push, so every window and tab converges on the same set.
 */
export function createDetachBridge(client: ChannelClient): DetachBridge {
  const service = createServiceProxy<IDetachService>(client, DETACH_CHANNEL);
  return {
    detachFragment: (...args) => service.open(...args),
    attach: (...args) => service.close(...args),
    focus: (...args) => service.focus(...args),
    listDetached: (...args) => service.list(...args),
    onDetachedChange: (callback) =>
      client.listen(DETACHED_CHANGED_EVENT, (data) => {
        callback(data as DetachedSurfaceInfo[]);
      }),
  };
}
